import { useState } from 'react';
import { 
  X, Brain, TrendingUp, TrendingDown, BarChart3, Sparkles, CheckCircle, AlertCircle,
  Clock, Calendar, Target, Zap, Activity, Award, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Timer, Flame, Shield, PieChart, GitBranch, Scale, Repeat, Layers, ChevronDown, Star
} from 'lucide-react';
import { defaultConfluences } from '../../utils/constants';

const AIInsightsModal = ({
  darkMode,
  aiAnalysis,
  allConfluences = [],
  onClose
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [activeMatrix, setActiveMatrix] = useState('direction');
  const [expandedConfluence, setExpandedConfluence] = useState(null);
  const [confluenceSortBy, setConfluenceSortBy] = useState('count');
  
  // Helper to get confluence name from ID
  const getConfluenceName = (id) => {
    const list = allConfluences.length > 0 ? allConfluences : defaultConfluences;
    const conf = list.find(c => c.id === id);
    return conf ? conf.name : id;
  };
  
  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'setups', label: 'Setups', icon: Target },
    { id: 'confluences', label: 'Confluences', icon: Layers },
    { id: 'matrices', label: 'Matrices', icon: GitBranch },
    { id: 'time', label: 'Time', icon: Clock },
    { id: 'risk', label: 'Risk', icon: Shield },
    { id: 'insights', label: 'AI Insights', icon: Sparkles }
  ];
  
  const formatHour = (hour) => {
    if (hour === 0) return '12am';
    if (hour === 12) return '12pm';
    return hour > 12 ? `${hour - 12}pm` : `${hour}am`;
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  // Mini equity curve component
  const MiniEquityCurve = ({ data, darkMode }) => {
    if (!data || data.length === 0) return null;
    
    const maxVal = Math.max(...data.map(d => d.cumulative), 0);
    const minVal = Math.min(...data.map(d => d.cumulative), 0);
    const range = maxVal - minVal || 1;
    const height = 120;
    const width = 100;
    
    // Normalize to percentage
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d.cumulative - minVal) / range) * height;
      return `${x},${y}`;
    }).join(' ');
    
    const zeroY = height - ((0 - minVal) / range) * height;
    
    return (
      <div className="relative">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-32">
          {/* Zero line */}
          <line 
            x1="0" y1={zeroY} x2={width} y2={zeroY} 
            stroke={darkMode ? '#374151' : '#E5E7EB'} 
            strokeWidth="0.5"
            strokeDasharray="2,2"
          />
          {/* Equity curve */}
          <polyline
            points={points}
            fill="none"
            stroke={data[data.length - 1]?.cumulative >= 0 ? '#10B981' : '#EF4444'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Area fill */}
          <polygon
            points={`0,${zeroY} ${points} ${width},${zeroY}`}
            fill={data[data.length - 1]?.cumulative >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)'}
          />
        </svg>
        <div className="flex justify-between text-xs mt-1">
          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Trade 1</span>
          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Trade {data.length}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className={`rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden transition-colors duration-300 ${
        darkMode ? 'bg-gray-950/95 backdrop-blur-2xl border border-white/10' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`p-6 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                darkMode ? 'bg-purple-900/30' : 'bg-purple-100'
              }`}>
                <Brain className="text-purple-500" size={24} />
              </div>
              <div>
                <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  AI Trading Insights
                </h2>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Analysis of {aiAnalysis.totalTrades} trades
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Overall Grade */}
              <div className={`text-center px-4 py-2 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className={`text-3xl font-black ${aiAnalysis.grade?.color || 'text-gray-500'}`}>
                  {aiAnalysis.grade?.letter || 'N/A'}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Overall</div>
              </div>
              <button 
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Tab Navigation — frosted-glass segmented control */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 whitespace-nowrap border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                  activeTab === tab.id
                    ? darkMode
                      ? 'bg-purple-500/25 border-purple-400/50 text-purple-50 ring-1 ring-inset ring-purple-400/30 shadow-[0_0_14px_rgba(168,85,247,0.25)]'
                      : 'bg-purple-50 border-purple-300 text-purple-700 ring-1 ring-inset ring-purple-200'
                    : darkMode
                      ? 'bg-transparent border-transparent text-gray-400 hover:bg-white/[0.06] hover:border-white/10 hover:text-gray-200'
                      : 'bg-transparent border-transparent text-gray-600 hover:bg-gray-100 hover:border-gray-200 hover:text-gray-900'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <div className={`text-3xl font-bold ${aiAnalysis.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {aiAnalysis.totalPnL >= 0 ? '+' : '-'}${Math.abs(aiAnalysis.totalPnL)?.toFixed(2)}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total P&L</div>
                </div>
                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <div className={`text-3xl font-bold ${parseFloat(aiAnalysis.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                    {aiAnalysis.winRate}%
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Win Rate</div>
                </div>
                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <div className={`text-3xl font-bold ${
                    parseFloat(aiAnalysis.profitFactor) >= 1.5 ? 'text-green-500' : 
                    parseFloat(aiAnalysis.profitFactor) >= 1 ? 'text-yellow-500' : 'text-red-500'
                  }`}>
                    {aiAnalysis.profitFactor}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Profit Factor</div>
                </div>
                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <div className={`text-3xl font-bold ${
                    parseFloat(aiAnalysis.avgR) >= 1 ? 'text-green-500' : 
                    parseFloat(aiAnalysis.avgR) >= 0 ? 'text-yellow-500' : 'text-red-500'
                  }`}>
                    {aiAnalysis.avgR}R
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg R-Multiple</div>
                </div>
              </div>
              
              {/* Equity Curve & Week Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Equity Curve */}
                {aiAnalysis.equityCurve && aiAnalysis.equityCurve.length > 1 && (
                  <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                    <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <Activity size={16} className="text-cyan-500" />
                      Equity Curve
                    </h3>
                    <MiniEquityCurve data={aiAnalysis.equityCurve} darkMode={darkMode} />
                    <div className="flex justify-between mt-3 text-sm">
                      <div>
                        <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Max DD: </span>
                        <span className="text-red-500 font-medium">${aiAnalysis.maxDrawdown?.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>DD%: </span>
                        <span className="text-red-500 font-medium">{aiAnalysis.maxDrawdownPercent}%</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* This Week vs Last Week */}
                {aiAnalysis.weekComparison && (
                  <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                    <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <Repeat size={16} className="text-purple-500" />
                      This Week vs Last Week
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                        <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          This Week
                        </div>
                        <div className={`text-xl font-bold ${aiAnalysis.weekComparison.thisWeek.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {aiAnalysis.weekComparison.thisWeek.pnl >= 0 ? '+' : '-'}${Math.abs(aiAnalysis.weekComparison.thisWeek.pnl).toFixed(2)}
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {aiAnalysis.weekComparison.thisWeek.trades} trades • {aiAnalysis.weekComparison.thisWeek.winRate}% WR
                        </div>
                      </div>
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                        <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Last Week
                        </div>
                        <div className={`text-xl font-bold ${aiAnalysis.weekComparison.lastWeek.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {aiAnalysis.weekComparison.lastWeek.pnl >= 0 ? '+' : '-'}${Math.abs(aiAnalysis.weekComparison.lastWeek.pnl).toFixed(2)}
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {aiAnalysis.weekComparison.lastWeek.trades} trades • {aiAnalysis.weekComparison.lastWeek.winRate}% WR
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Daily Stats & Streaks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Daily Stats */}
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Calendar size={16} className="text-blue-500" />
                    Daily Performance
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-500">{aiAnalysis.dailyStats?.greenDays}</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Green Days</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-500">{aiAnalysis.dailyStats?.redDays}</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Red Days</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${aiAnalysis.dailyStats?.avgDailyPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        ${aiAnalysis.dailyStats?.avgDailyPnL?.toFixed(0)}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Daily P&L</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {aiAnalysis.dailyStats?.avgTradesPerDay}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Trades/Day</div>
                    </div>
                  </div>
                </div>
                
                {/* Streaks */}
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Flame size={16} className="text-orange-500" />
                    Streaks
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${
                        aiAnalysis.streaks?.current > 0 ? 'text-green-500' : 
                        aiAnalysis.streaks?.current < 0 ? 'text-red-500' : 'text-gray-500'
                      }`}>
                        {aiAnalysis.streaks?.current > 0 ? '+' : ''}{aiAnalysis.streaks?.current}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-500">{aiAnalysis.streaks?.maxWin}</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Best Win</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-500">{aiAnalysis.streaks?.maxLoss}</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Worst Loss</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Direction Analysis */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Activity size={16} className="text-purple-500" />
                  Direction Analysis
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Long */}
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-900/20 border border-green-800/30' : 'bg-green-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium flex items-center gap-1 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                        <ArrowUpRight size={16} /> Long
                      </span>
                      <span className={`text-lg font-bold ${
                        parseFloat(aiAnalysis.directionAnalysis?.long?.winRate) >= 50 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {aiAnalysis.directionAnalysis?.long?.winRate}%
                      </span>
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {aiAnalysis.directionAnalysis?.long?.count} trades • 
                      <span className={aiAnalysis.directionAnalysis?.long?.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                        {' '}{aiAnalysis.directionAnalysis?.long?.totalPnL >= 0 ? '+' : '-'}${Math.abs(aiAnalysis.directionAnalysis?.long?.totalPnL)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {/* Short */}
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-900/20 border border-red-800/30' : 'bg-red-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium flex items-center gap-1 ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                        <ArrowDownRight size={16} /> Short
                      </span>
                      <span className={`text-lg font-bold ${
                        parseFloat(aiAnalysis.directionAnalysis?.short?.winRate) >= 50 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {aiAnalysis.directionAnalysis?.short?.winRate}%
                      </span>
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {aiAnalysis.directionAnalysis?.short?.count} trades • 
                      <span className={aiAnalysis.directionAnalysis?.short?.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                        {' '}{aiAnalysis.directionAnalysis?.short?.totalPnL >= 0 ? '+' : '-'}${Math.abs(aiAnalysis.directionAnalysis?.short?.totalPnL)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Revenge Trading Alert */}
              {aiAnalysis.revengeTrading && aiAnalysis.revengeTrading.instances > 0 && (
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200'}`}>
                  <h3 className={`font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-orange-400' : 'text-orange-800'}`}>
                    <AlertTriangle size={16} />
                    Revenge Trading Detected
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-500">{aiAnalysis.revengeTrading.instances}</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Instances</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${aiAnalysis.revengeTrading.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {aiAnalysis.revengeTrading.totalPnL >= 0 ? '' : '-'}${Math.abs(aiAnalysis.revengeTrading.totalPnL)?.toFixed(2)}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Impact</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${aiAnalysis.revengeTrading.avgPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {aiAnalysis.revengeTrading.avgPnL >= 0 ? '' : '-'}${Math.abs(aiAnalysis.revengeTrading.avgPnL)?.toFixed(2)}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg/Instance</div>
                    </div>
                  </div>
                  <p className={`text-sm mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Tip: Take a 5-10 minute break after a loss before entering your next trade.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* SETUPS TAB */}
          {activeTab === 'setups' && (
            <div className="space-y-6">
              {/* Best & Most Profitable */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiAnalysis.bestSetup && (
                  <div className={`p-4 rounded-xl border ${darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}`}>
                    <h3 className={`font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-800'}`}>
                      <Award size={20} />
                      Highest Win Rate
                    </h3>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                      {aiAnalysis.bestSetup[0]}
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {aiAnalysis.bestSetup[1].winRate}% win rate • {aiAnalysis.bestSetup[1].count} trades
                    </p>
                  </div>
                )}
                {aiAnalysis.mostProfitableSetup && (
                  <div className={`p-4 rounded-xl border ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                    <h3 className={`font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                      <TrendingUp size={20} />
                      Most Profitable
                    </h3>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {aiAnalysis.mostProfitableSetup[0]}
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      +${aiAnalysis.mostProfitableSetup[1].totalPnL.toFixed(2)} total • {aiAnalysis.mostProfitableSetup[1].count} trades
                    </p>
                  </div>
                )}
              </div>
              
              {/* All Setups */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Setup Performance Breakdown
                </h3>
                <div className="space-y-2">
                  {Object.entries(aiAnalysis.setupPerformance)
                    .sort((a, b) => b[1].totalPnL - a[1].totalPnL)
                    .map(([setup, data]) => (
                    <div key={setup} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{setup}</span>
                        <div className="flex gap-4 text-sm">
                          <span className={parseFloat(data.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}>
                            {data.winRate}% WR
                          </span>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {data.count} trades
                          </span>
                          <span className={data.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {data.totalPnL >= 0 ? '+' : '-'}${Math.abs(data.totalPnL).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-4 text-xs">
                        <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                          Avg: ${data.avgPnL}/trade
                        </span>
                        {parseFloat(data.avgRMultiple) > 0 && (
                          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                            Avg R: {data.avgRMultiple}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* ATM Strategies */}
              {aiAnalysis.atmPerformance?.length > 0 && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Zap size={16} className="text-yellow-500" />
                    ATM Strategy Performance
                  </h3>
                  <div className="space-y-2">
                    {aiAnalysis.atmPerformance.map(([atm, data]) => (
                      <div key={atm} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                        <div className="flex justify-between items-center">
                          <span className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{atm}</span>
                          <div className="flex gap-3 text-sm">
                            <span className={parseFloat(data.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}>
                              {data.winRate}%
                            </span>
                            <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                              {data.count}
                            </span>
                            <span className={data.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                              {data.totalPnL >= 0 ? '+' : '-'}${Math.abs(data.totalPnL).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Position Sizing */}
              {aiAnalysis.positionSizing && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Scale size={16} className="text-indigo-500" />
                    Position Sizing Analysis
                  </h3>
                  <div className="space-y-2">
                    {aiAnalysis.positionSizing.performance.map(data => (
                      <div key={data.bucket} className={`p-3 rounded-lg flex justify-between items-center ${
                        darkMode ? 'bg-gray-700/50' : 'bg-white'
                      } ${aiAnalysis.positionSizing.optimal?.bucket === data.bucket ? (darkMode ? 'ring-2 ring-green-500' : 'ring-2 ring-green-400') : ''}`}>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {data.bucket} contract{data.bucket !== '1' ? 's' : ''}
                          </span>
                          {aiAnalysis.positionSizing.optimal?.bucket === data.bucket && (
                            <span className="text-xs px-2 py-0.5 rounded-sm bg-green-500/20 text-green-400">OPTIMAL</span>
                          )}
                        </div>
                        <div className="flex gap-4 text-sm">
                          <span className={parseFloat(data.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}>
                            {data.winRate}% WR
                          </span>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {data.count} trades
                          </span>
                          <span className={data.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {data.totalPnL >= 0 ? '+' : '-'}${Math.abs(data.totalPnL).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* MATRICES TAB */}
          {activeTab === 'matrices' && (
            <div className="space-y-6">
              {/* Matrix Selector */}
              <div className="flex gap-2">
                {[
                  { id: 'direction', label: 'Setup × Direction' },
                  { id: 'time', label: 'Setup × Session' },
                  { id: 'symbol', label: 'Setup × Symbol' }
                ].map(m => (
                  <button
                    key={m.id}
                    onClick={() => setActiveMatrix(m.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                      activeMatrix === m.id
                        ? darkMode
                          ? 'bg-purple-500/25 border-purple-400/50 text-purple-50 ring-1 ring-inset ring-purple-400/30 shadow-[0_0_12px_rgba(168,85,247,0.25)]'
                          : 'bg-purple-50 border-purple-300 text-purple-700 ring-1 ring-inset ring-purple-200'
                        : darkMode
                          ? 'bg-white/[0.04] border-white/10 text-gray-400 hover:bg-white/[0.08] hover:border-white/20 hover:text-gray-200'
                          : 'bg-white/60 border-gray-200 text-gray-600 hover:bg-white/80 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              
              {/* Setup × Direction Matrix */}
              {activeMatrix === 'direction' && aiAnalysis.setupDirectionMatrix && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <GitBranch size={16} className="text-purple-500" />
                    Setup × Direction Performance
                  </h3>
                  <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    See which setups work best for long vs short trades
                  </p>
                  <div className="space-y-2">
                    {aiAnalysis.setupDirectionMatrix.filter(d => d.count >= 2).map((data, i) => (
                      <div key={i} className={`p-3 rounded-lg flex justify-between items-center ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{data.setup}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            data.direction === 'long' 
                              ? 'bg-green-500/20 text-green-400' 
                              : data.direction === 'short'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {data.direction?.toUpperCase() || 'N/A'}
                          </span>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <span className={parseFloat(data.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}>
                            {data.winRate}% WR
                          </span>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {data.count}
                          </span>
                          <span className={data.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {data.totalPnL >= 0 ? '+' : '-'}${Math.abs(data.totalPnL).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Setup × Session Matrix */}
              {activeMatrix === 'time' && aiAnalysis.setupTimeMatrix && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Clock size={16} className="text-blue-500" />
                    Setup × Session Performance
                  </h3>
                  <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    See which setups work best at different times of day
                  </p>
                  <div className="space-y-2">
                    {aiAnalysis.setupTimeMatrix.filter(d => d.count >= 2).map((data, i) => (
                      <div key={i} className={`p-3 rounded-lg flex justify-between items-center ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{data.setup}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            data.session === 'Morning' 
                              ? 'bg-yellow-500/20 text-yellow-400' 
                              : data.session === 'Midday'
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {data.session}
                          </span>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <span className={parseFloat(data.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}>
                            {data.winRate}% WR
                          </span>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {data.count}
                          </span>
                          <span className={data.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {data.totalPnL >= 0 ? '+' : '-'}${Math.abs(data.totalPnL).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Setup × Symbol Matrix */}
              {activeMatrix === 'symbol' && aiAnalysis.setupSymbolMatrix && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <BarChart3 size={16} className="text-cyan-500" />
                    Setup × Symbol Performance
                  </h3>
                  <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    See which setups work best on different instruments
                  </p>
                  <div className="space-y-2">
                    {aiAnalysis.setupSymbolMatrix.filter(d => d.count >= 2).map((data, i) => (
                      <div key={i} className={`p-3 rounded-lg flex justify-between items-center ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{data.setup}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm bg-cyan-500/20 text-cyan-400`}>
                            {data.symbol}
                          </span>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <span className={parseFloat(data.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}>
                            {data.winRate}% WR
                          </span>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {data.count}
                          </span>
                          <span className={data.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {data.totalPnL >= 0 ? '+' : '-'}${Math.abs(data.totalPnL).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* TIME TAB */}
          {activeTab === 'time' && (
            <div className="space-y-6">
              {/* Session Performance */}
              {aiAnalysis.timeAnalysis?.sessions && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Clock size={16} className="text-purple-500" />
                    Session Performance
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(aiAnalysis.timeAnalysis.sessions).map(([key, session]) => (
                      <div key={key} className={`p-4 rounded-lg text-center ${
                        darkMode ? 'bg-gray-700/50' : 'bg-white'
                      } ${session.count === 0 ? 'opacity-50' : ''}`}>
                        <div className={`font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {session.name.split('(')[0].trim()}
                        </div>
                        <div className={`text-xs mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {session.name.match(/\(([^)]+)\)/)?.[1]}
                        </div>
                        {session.count > 0 ? (
                          <>
                            <div className={`text-2xl font-bold ${session.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {session.totalPnL >= 0 ? '+' : '-'}${Math.abs(session.totalPnL).toFixed(0)}
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {session.count} trades • {session.winRate}% WR
                            </div>
                          </>
                        ) : (
                          <div className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No trades</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Best & Worst Hour */}
              {(aiAnalysis.timeAnalysis?.bestHour || aiAnalysis.timeAnalysis?.worstHour) && (
                <div className="grid grid-cols-2 gap-4">
                  {aiAnalysis.timeAnalysis?.bestHour && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-green-900/20 border border-green-800/30' : 'bg-green-50'}`}>
                      <h3 className={`font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                        <Award size={16} /> Best Hour
                      </h3>
                      <div className="text-3xl font-bold text-green-500">
                        {formatHour(aiAnalysis.timeAnalysis.bestHour.hour)}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        +${aiAnalysis.timeAnalysis.bestHour.totalPnL.toFixed(2)} from {aiAnalysis.timeAnalysis.bestHour.count} trades
                      </div>
                    </div>
                  )}
                  {aiAnalysis.timeAnalysis?.worstHour && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900/20 border border-red-800/30' : 'bg-red-50'}`}>
                      <h3 className={`font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                        <AlertTriangle size={16} /> Worst Hour
                      </h3>
                      <div className="text-3xl font-bold text-red-500">
                        {formatHour(aiAnalysis.timeAnalysis.worstHour.hour)}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        ${aiAnalysis.timeAnalysis.worstHour.totalPnL.toFixed(2)} from {aiAnalysis.timeAnalysis.worstHour.count} trades
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Day of Week */}
              {aiAnalysis.timeAnalysis?.dayOfWeek?.length > 0 && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Calendar size={16} className="text-blue-500" />
                    Day of Week Performance
                  </h3>
                  <div className="space-y-2">
                    {aiAnalysis.timeAnalysis.dayOfWeek.map(([_, day]) => (
                      <div key={day.name} className={`flex justify-between items-center p-3 rounded-lg ${
                        darkMode ? 'bg-gray-700/50' : 'bg-white'
                      }`}>
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{day.name}</span>
                        <div className="flex gap-4 text-sm">
                          <span className={parseFloat(day.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}>
                            {day.winRate}% WR
                          </span>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {day.count} trades
                          </span>
                          <span className={day.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {day.totalPnL >= 0 ? '+' : '-'}${Math.abs(day.totalPnL).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Best & Worst Days */}
              {(aiAnalysis.dailyStats?.bestDay || aiAnalysis.dailyStats?.worstDay) && (
                <div className="grid grid-cols-2 gap-4">
                  {aiAnalysis.dailyStats?.bestDay && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-green-900/20 border border-green-800/30' : 'bg-green-50'}`}>
                      <h3 className={`font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                        <TrendingUp size={16} /> Best Day
                      </h3>
                      <div className="text-2xl font-bold text-green-500">
                        +${aiAnalysis.dailyStats.bestDay.pnl.toFixed(2)}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {formatDate(aiAnalysis.dailyStats.bestDay.date)} • {aiAnalysis.dailyStats.bestDay.trades} trades
                      </div>
                    </div>
                  )}
                  {aiAnalysis.dailyStats?.worstDay && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900/20 border border-red-800/30' : 'bg-red-50'}`}>
                      <h3 className={`font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                        <TrendingDown size={16} /> Worst Day
                      </h3>
                      <div className="text-2xl font-bold text-red-500">
                        ${aiAnalysis.dailyStats.worstDay.pnl.toFixed(2)}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {formatDate(aiAnalysis.dailyStats.worstDay.date)} • {aiAnalysis.dailyStats.worstDay.trades} trades
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* CONFLUENCES TAB */}
          {activeTab === 'confluences' && (() => {
            const wrColor = (wr) => parseFloat(wr) >= 55 ? 'text-green-500' : parseFloat(wr) >= 45 ? 'text-yellow-500' : 'text-red-500';
            const pnlColor = (v) => v >= 0 ? 'text-green-500' : 'text-red-500';
            const fmtPnl = (v) => `${v >= 0 ? '+' : ''}$${Math.abs(v).toFixed(0)}`;
            const fmtHour = (h) => `${h > 12 ? h - 12 : h || 12}:00 ${h >= 12 ? 'PM' : 'AM'}`;
            const sortedConfs = [...(aiAnalysis.confluenceAnalysis?.allConfluences || aiAnalysis.confluenceAnalysis?.individual || [])]
              .filter(c => c.count >= 3)
              .sort((a, b) => {
                if (confluenceSortBy === 'winRate') return parseFloat(b.winRate) - parseFloat(a.winRate);
                if (confluenceSortBy === 'avgR') return parseFloat(b.avgR) - parseFloat(a.avgR);
                if (confluenceSortBy === 'totalPnL') return b.totalPnL - a.totalPnL;
                return b.count - a.count;
              });
            return (
            <div className="space-y-6">
              {/* Section A: Confluence Stacking Curve */}
              {aiAnalysis.confluenceAnalysis?.byCount?.length > 0 && (
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Confluence Stacking Curve
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {aiAnalysis.confluenceAnalysis.byCount.map((item, idx, arr) => {
                      const prevWr = idx > 0 ? parseFloat(arr[idx - 1].winRate) : null;
                      const curWr = parseFloat(item.winRate);
                      const trend = prevWr !== null ? (curWr > prevWr ? 'up' : curWr < prevWr ? 'down' : null) : null;
                      return (
                      <div key={item.bucket} className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {item.bucket === '0' ? 'None' : item.bucket}
                          {trend === 'up' && <ArrowUpRight size={14} className="inline ml-1 text-green-500" />}
                          {trend === 'down' && <ArrowDownRight size={14} className="inline ml-1 text-red-500" />}
                        </div>
                        <div className={`text-2xl font-bold mt-1 ${wrColor(item.winRate)}`}>
                          {item.winRate}%
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {item.count} trades · Avg R: {item.avgR || '—'}
                        </div>
                        <div className={`text-xs ${pnlColor(item.totalPnL)}`}>
                          {fmtPnl(parseFloat(item.avgPnL))} avg
                        </div>
                      </div>
                    );})}
                  </div>
                </div>
              )}

              {/* Section B: All Confluences (clickable, sortable) */}
              {sortedConfs.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      All Confluences
                    </h3>
                    <div className="flex gap-1">
                      {[{k:'count',l:'Count'},{k:'winRate',l:'WR%'},{k:'avgR',l:'Avg R'},{k:'totalPnL',l:'P&L'}].map(s => (
                        <button key={s.k} onClick={() => setConfluenceSortBy(s.k)}
                          className={`px-2 py-1 text-xs rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                            confluenceSortBy === s.k
                              ? darkMode
                                ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25'
                                : 'bg-blue-50 border-blue-300 text-blue-700 ring-1 ring-inset ring-blue-200'
                              : darkMode
                                ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20'
                                : 'bg-white/60 border-gray-200 text-gray-600 hover:bg-white/80 hover:border-gray-300'
                          }`}>
                          {s.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {sortedConfs.map(conf => {
                      const isExpanded = expandedConfluence === conf.id;
                      const confName = getConfluenceName(conf.id);
                      return (
                      <div key={conf.id}>
                        <button onClick={() => setExpandedConfluence(isExpanded ? null : conf.id)}
                          className={`w-full p-3 rounded-xl flex items-center justify-between transition-colors ${
                            isExpanded
                              ? darkMode ? 'bg-gray-600/70 ring-1 ring-blue-500/50' : 'bg-blue-50 ring-1 ring-blue-300'
                              : darkMode ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`}>
                          <div className="flex items-center gap-3">
                            <ChevronDown size={16} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            <div className="text-left">
                              <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{confName}</div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{conf.count} trades</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-right">
                            <div>
                              <div className={`text-lg font-bold ${wrColor(conf.winRate)}`}>{conf.winRate}%</div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>WR</div>
                            </div>
                            <div>
                              <div className={`text-lg font-bold ${parseFloat(conf.avgR) >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>{conf.avgR}R</div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg</div>
                            </div>
                            <div>
                              <div className={`text-lg font-bold ${pnlColor(conf.totalPnL)}`}>{fmtPnl(conf.totalPnL)}</div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total</div>
                            </div>
                          </div>
                        </button>

                        {/* Section D: Expanded Drill-Down */}
                        {isExpanded && (
                          <div className={`mx-2 mt-1 mb-2 p-4 rounded-xl space-y-4 ${darkMode ? 'bg-gray-800/80 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                            {/* D1: Direction Split */}
                            <div>
                              <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Direction Split</h4>
                              <div className="grid grid-cols-2 gap-3">
                                {['long', 'short'].map(dir => {
                                  const d = conf[dir];
                                  if (!d || d.count === 0) return (
                                    <div key={dir} className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                      <div className={`text-sm font-semibold ${dir === 'long' ? 'text-blue-400' : 'text-red-400'}`}>{dir === 'long' ? 'Long' : 'Short'}</div>
                                      <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No trades</div>
                                    </div>
                                  );
                                  const otherDir = dir === 'long' ? 'short' : 'long';
                                  const other = conf[otherDir];
                                  const isStronger = other && other.count >= 2 && d.count >= 2 && parseFloat(d.avgR) > parseFloat(other.avgR);
                                  return (
                                    <div key={dir} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <span className={`text-sm font-semibold ${dir === 'long' ? 'text-blue-400' : 'text-red-400'}`}>
                                          {dir === 'long' ? 'Long' : 'Short'}
                                        </span>
                                        {isStronger && <span className="text-xs px-1.5 py-0.5 rounded-sm bg-green-500/20 text-green-400">Stronger</span>}
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 text-center">
                                        <div>
                                          <div className={`text-lg font-bold ${wrColor(d.winRate)}`}>{d.winRate}%</div>
                                          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{d.count} trades</div>
                                        </div>
                                        <div>
                                          <div className={`text-lg font-bold ${parseFloat(d.avgR) >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>{d.avgR}R</div>
                                          <div className={`text-xs ${pnlColor(d.totalPnL)}`}>{fmtPnl(parseFloat(d.avgPnL))} avg</div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* D2: Time of Day */}
                            {conf.hourlyArray && conf.hourlyArray.length > 0 && (
                              <div>
                                <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Performance by Hour</h4>
                                <div className="flex flex-wrap gap-2">
                                  {(() => {
                                    const maxPnl = Math.max(...conf.hourlyArray.map(h => Math.abs(h.totalPnL)), 1);
                                    const bestHour = conf.hourlyArray.reduce((best, h) => parseFloat(h.avgR) > parseFloat(best.avgR) ? h : best, conf.hourlyArray[0]);
                                    return conf.hourlyArray.map(h => {
                                      const intensity = Math.min(Math.abs(h.totalPnL) / maxPnl, 1);
                                      const bg = h.totalPnL >= 0
                                        ? `rgba(34, 197, 94, ${0.1 + intensity * 0.3})`
                                        : `rgba(239, 68, 68, ${0.1 + intensity * 0.3})`;
                                      return (
                                        <div key={h.hour} className="p-2 rounded-lg text-center min-w-[70px]" style={{ backgroundColor: bg }}>
                                          <div className="flex items-center justify-center gap-1">
                                            <span className={`text-xs font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{fmtHour(h.hour)}</span>
                                            {h.hour === bestHour.hour && conf.hourlyArray.length > 1 && <Star size={10} className="text-yellow-400 fill-yellow-400" />}
                                          </div>
                                          <div className={`text-sm font-bold ${wrColor(h.winRate)}`}>{h.winRate}%</div>
                                          <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{h.count} {h.count === 1 ? 'Trade' : 'Trades'} · {h.avgR}R</div>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                            )}

                            {/* D3: Top ATMs */}
                            {conf.atmArray && conf.atmArray.length > 0 && (
                              <div>
                                <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Top ATM Pairings</h4>
                                <div className="space-y-1">
                                  {conf.atmArray.slice(0, 5).map((atm, idx) => (
                                    <div key={atm.atm} className={`flex items-center justify-between p-2 rounded-lg ${darkMode ? 'bg-gray-700/40' : 'bg-gray-50'}`}>
                                      <div className="flex items-center gap-2">
                                        <span className={`text-xs font-bold ${idx === 0 ? 'text-yellow-500' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}>#{idx + 1}</span>
                                        <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{atm.atm}</span>
                                      </div>
                                      <div className="flex items-center gap-3 text-sm">
                                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{atm.count} {atm.count === 1 ? 'Trade' : 'Trades'}</span>
                                        <span className={`font-semibold ${wrColor(atm.winRate)}`}>{atm.winRate}%</span>
                                        <span className={`font-semibold ${parseFloat(atm.avgR) >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>{atm.avgR}R</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* D4: Entry Quality (MAE/MFE) */}
                            {(conf.avgMAE !== null || conf.avgMFE !== null) && (
                              <div>
                                <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Entry Quality</h4>
                                <div className="grid grid-cols-3 gap-3">
                                  <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Heat (MAE)</div>
                                    <div className="text-lg font-bold text-red-400">
                                      {conf.avgMAE !== null ? `$${Math.abs(conf.avgMAE).toFixed(0)}` : '—'}
                                    </div>
                                    {conf.maeCount > 0 && <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{conf.maeCount} trades</div>}
                                  </div>
                                  <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Run (MFE)</div>
                                    <div className="text-lg font-bold text-green-400">
                                      {conf.avgMFE !== null ? `$${conf.avgMFE.toFixed(0)}` : '—'}
                                    </div>
                                    {conf.mfeCount > 0 && <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{conf.mfeCount} trades</div>}
                                  </div>
                                  <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Capture Ratio</div>
                                    <div className={`text-lg font-bold ${conf.mfeMaeRatio && parseFloat(conf.mfeMaeRatio) >= 2 ? 'text-green-400' : conf.mfeMaeRatio && parseFloat(conf.mfeMaeRatio) >= 1 ? 'text-yellow-400' : 'text-red-400'}`}>
                                      {conf.mfeMaeRatio ? `${conf.mfeMaeRatio}x` : '—'}
                                    </div>
                                    <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>MFE/MAE</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );})}
                  </div>
                </div>
              )}

              {/* Section C: Direction Matrix */}
              {sortedConfs.length > 0 && (
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Direction Matrix
                  </h3>
                  <div className={`rounded-xl overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}>
                          <th className={`text-left p-3 font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Confluence</th>
                          <th className={`text-center p-3 font-semibold text-blue-400`} colSpan={2}>Long</th>
                          <th className={`text-center p-3 font-semibold text-red-400`} colSpan={2}>Short</th>
                        </tr>
                        <tr className={darkMode ? 'bg-gray-700/30' : 'bg-gray-50/50'}>
                          <th></th>
                          <th className={`text-center px-3 pb-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>WR%</th>
                          <th className={`text-center px-3 pb-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg R</th>
                          <th className={`text-center px-3 pb-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>WR%</th>
                          <th className={`text-center px-3 pb-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg R</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedConfs.slice(0, 15).map((conf, idx) => (
                          <tr key={conf.id} className={`${idx % 2 === 0 ? (darkMode ? 'bg-gray-800/30' : 'bg-white') : (darkMode ? 'bg-gray-800/10' : 'bg-gray-50/30')} border-t ${darkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                            <td className={`p-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{getConfluenceName(conf.id)}</td>
                            <td className={`text-center p-3 font-semibold ${conf.long?.count >= 2 ? wrColor(conf.long.winRate) : darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                              {conf.long?.count >= 2 ? `${conf.long.winRate}%` : '—'}
                            </td>
                            <td className={`text-center p-3 font-semibold ${conf.long?.count >= 2 ? (parseFloat(conf.long.avgR) >= 0 ? 'text-cyan-400' : 'text-red-400') : darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                              {conf.long?.count >= 2 ? `${conf.long.avgR}` : '—'}
                            </td>
                            <td className={`text-center p-3 font-semibold ${conf.short?.count >= 2 ? wrColor(conf.short.winRate) : darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                              {conf.short?.count >= 2 ? `${conf.short.winRate}%` : '—'}
                            </td>
                            <td className={`text-center p-3 font-semibold ${conf.short?.count >= 2 ? (parseFloat(conf.short.avgR) >= 0 ? 'text-cyan-400' : 'text-red-400') : darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                              {conf.short?.count >= 2 ? `${conf.short.avgR}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Section E: Best Confluence Combinations */}
              {aiAnalysis.confluenceAnalysis?.combinations?.length > 0 && (
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Best Confluence Combinations
                  </h3>
                  <div className="space-y-2">
                    {aiAnalysis.confluenceAnalysis.combinations.slice(0, 6).map((combo, idx) => (
                      <div key={combo.pair} className={`p-4 rounded-xl flex items-center justify-between ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          <span className={`text-2xl font-bold ${idx === 0 ? 'text-yellow-500' : idx === 1 ? 'text-gray-400' : idx === 2 ? 'text-amber-600' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            #{idx + 1}
                          </span>
                          <div>
                            <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {getConfluenceName(combo.ids[0])} + {getConfluenceName(combo.ids[1])}
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {combo.count} trades
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-xl font-bold ${wrColor(combo.winRate)}`}>
                            {combo.winRate}% WR
                          </div>
                          <div className={`text-xs ${pnlColor(combo.totalPnL)}`}>
                            {fmtPnl(combo.totalPnL)} total
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Confluence Data Message */}
              {(!aiAnalysis.confluenceAnalysis?.individual?.length && !aiAnalysis.confluenceAnalysis?.byCount?.some(b => b.bucket !== '0' && b.count > 0)) && (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Layers size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No Confluence Data Yet</p>
                  <p className="text-sm mt-1">Start tagging your trades with confluences to see analytics here!</p>
                </div>
              )}
            </div>
            );
          })()}
          
          {/* RISK TAB */}
          {activeTab === 'risk' && (
            <div className="space-y-6">
              {/* Risk Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <div className={`text-2xl font-bold ${parseFloat(aiAnalysis.avgR) >= 1 ? 'text-green-500' : parseFloat(aiAnalysis.avgR) >= 0 ? 'text-yellow-500' : 'text-red-500'}`}>
                    {aiAnalysis.avgR}R
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg R-Multiple</div>
                </div>
                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <div className={`text-2xl font-bold ${parseFloat(aiAnalysis.totalR) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {aiAnalysis.totalR}R
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total R</div>
                </div>
                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <div className="text-2xl font-bold text-green-500">
                    ${aiAnalysis.avgWin?.toFixed(2)}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Win</div>
                </div>
                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <div className="text-2xl font-bold text-red-500">
                    ${aiAnalysis.avgLoss?.toFixed(2)}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Loss</div>
                </div>
              </div>
              
              {/* Max Drawdown */}
              {aiAnalysis.maxDrawdown > 0 && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <TrendingDown size={16} className="text-red-500" />
                    Maximum Drawdown
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-red-500">${aiAnalysis.maxDrawdown?.toFixed(2)}</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dollar Amount</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-red-500">{aiAnalysis.maxDrawdownPercent}%</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>From Peak</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Mistakes Analysis */}
              {aiAnalysis.topMistakes?.length > 0 && (
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-red-400' : 'text-red-800'}`}>
                    <AlertTriangle size={16} />
                    Most Common Mistakes
                  </h3>
                  <div className="space-y-2">
                    {aiAnalysis.topMistakes.map(([mistake, count], index) => (
                      <div key={mistake} className="flex justify-between items-center">
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          {index + 1}. {mistake}
                        </span>
                        <span className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                          {count} times
                        </span>
                      </div>
                    ))}
                  </div>
                  {aiAnalysis.mistakeStats && (
                    <div className={`mt-4 pt-3 border-t ${darkMode ? 'border-red-800/50' : 'border-red-200'}`}>
                      <div className="grid grid-cols-3 gap-2 text-center text-sm">
                        <div>
                          <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Trades w/ Mistakes</div>
                          <div className="font-bold text-red-500">{aiAnalysis.mistakeStats.tradesWithMistakes}</div>
                        </div>
                        <div>
                          <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Mistake Rate</div>
                          <div className="font-bold text-red-500">{aiAnalysis.mistakeStats.mistakeRate}%</div>
                        </div>
                        <div>
                          <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Mistake P&L</div>
                          <div className={`font-bold ${aiAnalysis.mistakeStats.mistakePnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            ${aiAnalysis.mistakeStats.mistakePnL.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Recent vs Historical */}
              {aiAnalysis.recentPerformance && aiAnalysis.olderPerformance && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Timer size={16} className="text-cyan-500" />
                    Recent vs Historical Performance
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                      <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Last 20 Trades
                      </div>
                      <div className={`text-2xl font-bold ${aiAnalysis.recentPerformance.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {aiAnalysis.recentPerformance.pnl >= 0 ? '+' : '-'}${Math.abs(aiAnalysis.recentPerformance.pnl).toFixed(2)}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {aiAnalysis.recentPerformance.winRate}% WR
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                      <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Previous ({aiAnalysis.olderPerformance.count} trades)
                      </div>
                      <div className={`text-2xl font-bold ${aiAnalysis.olderPerformance.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {aiAnalysis.olderPerformance.pnl >= 0 ? '+' : '-'}${Math.abs(aiAnalysis.olderPerformance.pnl).toFixed(2)}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {aiAnalysis.olderPerformance.winRate}% WR
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* INSIGHTS TAB */}
          {activeTab === 'insights' && (
            <div className="space-y-6">
              {aiAnalysis.suggestions?.length > 0 ? (
                <>
                  {/* Positive Insights Section */}
                  {aiAnalysis.suggestions.filter(s => s.type === 'success').length > 0 && (
                    <div>
                      <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                        <CheckCircle size={16} />
                        Your Edge
                      </h3>
                      <div className="grid gap-3">
                        {aiAnalysis.suggestions.filter(s => s.type === 'success').map((suggestion, index) => (
                          <div key={index} className={`p-4 rounded-xl border ${
                            darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'
                          }`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                    darkMode ? 'bg-green-800/50 text-green-300' : 'bg-green-200 text-green-800'
                                  }`}>
                                    {suggestion.category}
                                  </span>
                                </div>
                                <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {suggestion.title || suggestion.message}
                                </h4>
                                {suggestion.title && (
                                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {suggestion.message}
                                  </p>
                                )}
                                {suggestion.details && (
                                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {suggestion.details}
                                  </p>
                                )}
                                {suggestion.action && (
                                  <p className={`text-xs mt-2 font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                                    → {suggestion.action}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Actionable Insights Section */}
                  {aiAnalysis.suggestions.filter(s => s.type === 'info').length > 0 && (
                    <div>
                      <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                        <Brain size={16} />
                        Opportunities
                      </h3>
                      <div className="grid gap-3">
                        {aiAnalysis.suggestions.filter(s => s.type === 'info').map((suggestion, index) => (
                          <div key={index} className={`p-4 rounded-xl border ${
                            darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
                          }`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                    darkMode ? 'bg-blue-800/50 text-blue-300' : 'bg-blue-200 text-blue-800'
                                  }`}>
                                    {suggestion.category}
                                  </span>
                                </div>
                                <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {suggestion.title || suggestion.message}
                                </h4>
                                {suggestion.title && (
                                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {suggestion.message}
                                  </p>
                                )}
                                {suggestion.details && (
                                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {suggestion.details}
                                  </p>
                                )}
                                {suggestion.action && (
                                  <p className={`text-xs mt-2 font-medium ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                    → {suggestion.action}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Warnings Section */}
                  {aiAnalysis.suggestions.filter(s => s.type === 'warning').length > 0 && (
                    <div>
                      <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                        <AlertCircle size={16} />
                        Areas to Improve
                      </h3>
                      <div className="grid gap-3">
                        {aiAnalysis.suggestions.filter(s => s.type === 'warning').map((suggestion, index) => (
                          <div key={index} className={`p-4 rounded-xl border ${
                            darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'
                          }`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                    darkMode ? 'bg-yellow-800/50 text-yellow-300' : 'bg-yellow-200 text-yellow-800'
                                  }`}>
                                    {suggestion.category}
                                  </span>
                                </div>
                                <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {suggestion.title || suggestion.message}
                                </h4>
                                {suggestion.title && (
                                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {suggestion.message}
                                  </p>
                                )}
                                {suggestion.details && (
                                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {suggestion.details}
                                  </p>
                                )}
                                {suggestion.action && (
                                  <p className={`text-xs mt-2 font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                                    → {suggestion.action}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <Sparkles size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Keep trading to unlock more AI insights!</p>
                </div>
              )}
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default AIInsightsModal;
